import { MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteRun } from "@/hooks/useMaxwellRuns";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { AgentRun } from "@/types/maxwell-runs";

import { RunStatusBadge } from "./RunStatusBadge";

/** Number of planned steps carried in a run's `plan` (`{ steps: [...] }`). */
function planStepCount(plan: Record<string, unknown>): number {
  const steps = (plan as { steps?: unknown }).steps;
  return Array.isArray(steps) ? steps.length : 0;
}

interface RunListItemProps {
  run: AgentRun;
  selected: boolean;
  onSelect: (id: string) => void;
  /** Fires after this row's run is deleted — lets the parent clear its
   *  selection when the deleted run was the one showing in the timeline. */
  onDeleted: () => void;
}

/** One row in the Activity run list. The goal leads — it's what the user
 *  recognizes — with status, an optional Bulk marker, and step-count/updated
 *  meta beneath. The selected row carries a primary left stripe. The row menu
 *  offers Delete for any run (confirmed — it permanently removes the run and
 *  its step history). */
export function RunListItem({ run, selected, onSelect, onDeleted }: RunListItemProps) {
  const steps = planStepCount(run.plan);
  const confirm = useConfirm();
  const del = useDeleteRun();

  const onDelete = async () => {
    const ok = await confirm({
      title: "Delete this run?",
      description:
        "The run and its step history are permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(run.id, {
      onSuccess: () => {
        toast.success("Run deleted");
        onDeleted();
      },
      onError: () => toast.error("Failed to delete run"),
    });
  };

  return (
    <div
      className={cn(
        "relative flex w-full items-start gap-1 px-4 py-3 transition-colors hover:bg-row-hover",
        selected && "bg-row-hover",
      )}
    >
      {selected && (
        <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-primary" />
      )}
      <button
        type="button"
        onClick={() => onSelect(run.id)}
        className="flex min-w-0 flex-1 flex-col gap-1.5 text-left"
      >
        <p className="line-clamp-2 text-sm font-medium text-foreground">{run.goal}</p>
        <div className="flex flex-wrap items-center gap-2">
          <RunStatusBadge status={run.status} />
          {run.kind === "bulk" && <Badge>Bulk</Badge>}
          <span className="text-xs text-muted-foreground">
            {steps} {steps === 1 ? "step" : "steps"} · {formatDateTime(run.updated_at)}
          </span>
        </div>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="Run actions"
          >
            <MoreHorizontal size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem destructive onSelect={onDelete}>
            <Trash2 size={14} />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
